import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

type NotificationLog = {
  id: number;
  title: string;
  body: string;
  link: string;
  status: 'sent' | 'failed';
  errorMessage: string;
  sentAt: string;
};

const EMPTY_FORM = { title: '', body: '', link: '' };

export default function ManageNotification({ authChurch }: { authChurch: string }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const fetchLogs = () => {
    setLogsLoading(true);
    axios
      .get(`${MainURL}/notification/logs`, { params: { authChurch } })
      .then((res) => setLogs(res.data?.data ?? []))
      .catch((err) => console.error('GET /notification/logs', err))
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const submitSend = () => {
    if (!form.title.trim() || !form.body.trim()) return;
    if (!window.confirm('이 알림을 앱 사용자 전체에게 발송할까요?')) return;

    setSending(true);
    setResultMessage(null);
    axios
      .post(`${MainURL}/notification/send`, { authChurch, ...form })
      .then(() => {
        setResultMessage({ type: 'success', text: '발송되었습니다.' });
        setForm(EMPTY_FORM);
        fetchLogs();
      })
      .catch((err) => {
        console.error('POST /notification/send', err);
        setResultMessage({
          type: 'error',
          text: err.response?.data?.message || '발송에 실패했습니다.',
        });
      })
      .finally(() => setSending(false));
  };

  const successCount = logs.filter((log) => log.status === 'sent').length;
  const failedCount = logs.filter((log) => log.status === 'failed').length;
  const currentYearMonth = new Date().toISOString().slice(0, 7);
  const thisMonthCount = logs.filter((log) => log.sentAt?.slice(0, 7) === currentYearMonth).length;

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">알림보내기</h2>
          <p className="manage-panel-desc">앱 푸시와 문자 메시지를 교인들에게 발송합니다.</p>
        </div>
      </div>

      <div className="manage-stats">
        <div className="manage-stat-card">
          <span>전체 발송</span>
          <strong>{logs.length}<small> 건</small></strong>
        </div>
        <div className="manage-stat-card">
          <span>이번 달 발송</span>
          <strong>{thisMonthCount}<small> 건</small></strong>
        </div>
        <div className="manage-stat-card">
          <span>발송 성공</span>
          <strong>{successCount}<small> 건</small></strong>
        </div>
        <div className="manage-stat-card">
          <span>발송 실패</span>
          <strong>{failedCount}<small> 건</small></strong>
        </div>
      </div>

      <div className="manage-grid-2col">
        <section className="manage-card-block">
          <div className="manage-block-head">
            <h3 className="manage-block-title">새 알림 작성</h3>
          </div>
          <div className="manage-block-body">
            <div className="manage-form">
              <label className="manage-field">
                <span>알림 제목</span>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="예: 이번 주 주보 안내" />
              </label>
              <label className="manage-field">
                <span>알림 내용</span>
                <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="알림에 표시될 내용을 입력하세요" />
                <span className="manage-notification-hint">현재 {form.body.length}자</span>
              </label>
              <label className="manage-field">
                <span>연결 링크 (선택)</span>
                <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="알림 클릭 시 이동할 화면/URL" />
              </label>
              <div className="manage-form-actions">
                <button
                  type="button"
                  className="manage-btn manage-btn--primary"
                  onClick={submitSend}
                  disabled={sending || !form.title.trim() || !form.body.trim()}
                >
                  {sending ? '발송 중...' : '🚀 발송'}
                </button>
                {resultMessage && (
                  <span className={resultMessage.type === 'error' ? 'manage-inline-message manage-inline-message--error' : 'manage-inline-message'}>
                    {resultMessage.text}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <aside className="manage-side-stack">
          <div className="manage-side-card manage-worship-phone">
            <div className="manage-worship-phone-notch">
              발송 전 미리보기
              <small>실시간 미리보기</small>
            </div>
            <div className="manage-worship-phone-screen">
              <div className="manage-notification-push-card">
                <div className="manage-notification-push-head">
                  <span className="manage-notification-push-appicon">⛪</span>
                  <span className="manage-notification-push-appname">앱 알림</span>
                  <span className="manage-notification-push-now">지금</span>
                </div>
                <div className="manage-notification-push-title">{form.title || '알림 제목을 입력하세요'}</div>
                <div className="manage-notification-push-body">{form.body || '알림 내용을 입력하면 이곳에 표시됩니다.'}</div>
              </div>
            </div>
          </div>

          <div className="manage-side-card">
            <div className="manage-side-card-head">발송 안내</div>
            <div className="manage-side-card-body manage-tip-list">
              <div className="manage-tip">
                <span className="manage-tip-dot" />
                <span>제목과 내용을 모두 입력해야 발송할 수 있습니다.</span>
              </div>
              <div className="manage-tip">
                <span className="manage-tip-dot" />
                <span>발송 후에는 취소할 수 없으니 내용을 다시 확인해 주세요.</span>
              </div>
              <div className="manage-tip">
                <span className="manage-tip-dot" />
                <span>연결 링크를 입력하면 알림을 눌렀을 때 해당 화면으로 이동합니다.</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <section className="manage-card-block">
        <div className="manage-block-head">
          <h3 className="manage-block-title">발송 이력</h3>
          <span className="manage-block-count">{logs.length}</span>
        </div>
        {logsLoading && <div className="manage-state">불러오는 중입니다...</div>}
        {!logsLoading && (
          <div className="manage-table-wrap">
            <table className="manage-table">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>발송일시</th>
                  <th>메시지</th>
                  <th style={{ width: 90 }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={3} className="manage-table-empty">발송 이력이 없습니다.</td>
                  </tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="manage-table-meta">{log.sentAt.replace('T', ' ').slice(0, 16)}</td>
                    <td>
                      <div className="manage-notification-msg-cell">
                        <b>{log.title}</b>
                        <small>{log.body}</small>
                      </div>
                    </td>
                    <td>
                      <span className={`manage-badge ${log.status === 'sent' ? 'manage-badge--green' : 'manage-badge--red'}`}>
                        {log.status === 'sent' ? '성공' : '실패'}
                      </span>
                      {log.status === 'failed' && log.errorMessage && (
                        <span className="manage-notification-error-msg">{log.errorMessage}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
